import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Settings, Code, X, Info, MessageCircle, Building, Save, ImageOff, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettings = () => {
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({
    field_name: '',
    field_key: '',
    field_type: 'text',
    options: [],
    required: false
  });
  const [newOption, setNewOption] = useState('');
  
  // Site Settings State
  const [siteSettings, setSiteSettings] = useState({
    whatsapp_number: '',
    company_name: '',
    company_email: '',
    company_phone: '',
    company_address: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  const runImageMigration = async () => {
    if (!window.confirm('This will rewrite all listing image URLs to point to Cloudflare R2. Existing R2-hosted images stay the same. Continue?')) {
      return;
    }
    setMigrating(true);
    setMigrationResult(null);
    try {
      const response = await axios.post(`${API}/admin/migrate-images-to-r2`);
      setMigrationResult(response.data);
      toast.success(`Migration complete! Rewrote ${response.data.urls_rewritten} image URLs across ${response.data.listings_updated} listings.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Migration failed. Check server logs.');
    } finally {
      setMigrating(false);
    }
  };

  useEffect(() => {
    fetchCustomFields();
    fetchSiteSettings();
  }, []);

  const fetchCustomFields = async () => {
    try {
      const response = await axios.get(`${API}/custom-fields/`);
      setCustomFields(response.data);
    } catch (error) {
      console.error('Failed to fetch custom fields:', error);
      toast.error('Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/`);
      setSiteSettings({
        whatsapp_number: response.data.whatsapp_number || '',
        company_name: response.data.company_name || '',
        company_email: response.data.company_email || '',
        company_phone: response.data.company_phone || '',
        company_address: response.data.company_address || ''
      });
    } catch (error) {
      console.error('Failed to fetch site settings:', error);
    }
  };

  const saveSiteSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/settings/`, siteSettings);
      toast.success('Site settings saved successfully');
    } catch (error) {
      toast.error('Failed to save site settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = {
      ...formData,
      options: formData.field_type === 'select' ? formData.options : null
    };

    try {
      if (editingField) {
        await axios.put(`${API}/custom-fields/${editingField.id}`, data);
        toast.success('Custom field updated successfully');
      } else {
        await axios.post(`${API}/custom-fields/`, data);
        toast.success('Custom field created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchCustomFields();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save custom field');
    }
  };

  const handleEdit = (field) => {
    setEditingField(field);
    setFormData({
      field_name: field.field_name,
      field_key: field.field_key,
      field_type: field.field_type,
      options: field.options || [],
      required: field.required
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this custom field?')) return;
    
    try {
      await axios.delete(`${API}/custom-fields/${id}`);
      toast.success('Custom field deleted successfully');
      fetchCustomFields();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete custom field');
    }
  };

  const resetForm = () => {
    setEditingField(null);
    setFormData({
      field_name: '',
      field_key: '',
      field_type: 'text',
      options: [],
      required: false
    });
    setNewOption('');
  };

  const addOption = () => {
    if (newOption && !formData.options.includes(newOption)) {
      setFormData({ ...formData, options: [...formData.options, newOption] });
      setNewOption('');
    }
  };

  const removeOption = (option) => {
    setFormData({ ...formData, options: formData.options.filter(o => o !== option) });
  };

  const generateFieldKey = (name) => {
    return name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Site Settings Section */}
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">
            Settings
          </h1>
          <p className="font-body text-muted-foreground mt-1">
            Configure your site and property listing options
          </p>
        </div>

        <Card className="rounded-none border-border">
          <CardHeader>
            <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
              <Building className="w-5 h-5" />
              Site Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* WhatsApp Number */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-green-500" />
                  WhatsApp Number
                </Label>
                <Input
                  value={siteSettings.whatsapp_number}
                  onChange={(e) => setSiteSettings({ ...siteSettings, whatsapp_number: e.target.value })}
                  placeholder="+961 71 123 456"
                  className="rounded-none border-2"
                  data-testid="whatsapp-number-input"
                />
                <p className="text-xs text-muted-foreground">
                  Include country code. This number will be used for the floating WhatsApp button on your site.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Company Phone
                </Label>
                <Input
                  value={siteSettings.company_phone}
                  onChange={(e) => setSiteSettings({ ...siteSettings, company_phone: e.target.value })}
                  placeholder="+961 1 234 567"
                  className="rounded-none border-2"
                  data-testid="company-phone-input"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Company Name
                </Label>
                <Input
                  value={siteSettings.company_name}
                  onChange={(e) => setSiteSettings({ ...siteSettings, company_name: e.target.value })}
                  placeholder="Signé Properties"
                  className="rounded-none border-2"
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Company Email
                </Label>
                <Input
                  type="email"
                  value={siteSettings.company_email}
                  onChange={(e) => setSiteSettings({ ...siteSettings, company_email: e.target.value })}
                  placeholder="contact@signeproperties.com"
                  className="rounded-none border-2"
                  data-testid="company-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                Company Address
              </Label>
              <Input
                value={siteSettings.company_address}
                onChange={(e) => setSiteSettings({ ...siteSettings, company_address: e.target.value })}
                placeholder="123 Main Street, Beirut, Lebanon"
                className="rounded-none border-2"
                data-testid="company-address-input"
              />
            </div>

            <div className="pt-4 border-t border-border">
              <Button 
                onClick={saveSiteSettings} 
                disabled={savingSettings}
                className="rounded-none font-heading font-bold uppercase tracking-wide bg-brand-gold text-brand-brown hover:bg-brand-gold/90"
                data-testid="save-settings-btn"
              >
                {savingSettings ? (
                  <div className="w-4 h-4 border-2 border-brand-brown border-t-transparent rounded-full animate-spin mr-2"></div>
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Settings
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Image Recovery Card */}
        <Card className="rounded-none border-2 border-amber-300 bg-amber-50/40">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <ImageOff className="w-5 h-5 text-amber-700" />
              Image Recovery
            </CardTitle>
            <p className="font-body text-sm text-muted-foreground">
              Listings created before the move to cloud storage may reference image files that are no longer on the server.
              Run this one-time recovery to point those listings at the cloud-stored copies (Cloudflare R2). Safe to run multiple times — it only rewrites URLs that can be matched.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={runImageMigration}
              disabled={migrating}
              className="rounded-none font-heading font-bold uppercase tracking-wide bg-amber-600 text-white hover:bg-amber-700"
              data-testid="run-image-migration-btn"
            >
              {migrating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Running...
                </>
              ) : (
                <>
                  <ImageOff className="w-4 h-4 mr-2" />
                  Recover Listing Images
                </>
              )}
            </Button>

            {migrationResult && (
              <div className="bg-white border-2 border-border p-4 space-y-2 text-sm font-body">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Listings Updated</p>
                    <p className="font-heading text-2xl font-bold">{migrationResult.listings_updated}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">URLs Rewritten</p>
                    <p className="font-heading text-2xl font-bold">{migrationResult.urls_rewritten}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Files in Cloud</p>
                    <p className="font-heading text-2xl font-bold">{migrationResult.r2_files_count}</p>
                  </div>
                </div>

                {migrationResult.listings_with_missing_files?.length > 0 && (
                  <div className="pt-3 border-t border-border space-y-2">
                    <p className="font-heading font-bold uppercase text-xs tracking-widest text-amber-700 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {migrationResult.listings_with_missing_files.length} Listing(s) Need Re-Upload
                    </p>
                    <p className="text-xs text-muted-foreground">
                      The image files below could not be found in cloud storage. Edit each listing and re-upload the images:
                    </p>
                    <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
                      {migrationResult.listings_with_missing_files.map((l) => (
                        <li key={l.listing_id} className="flex justify-between gap-2 py-1 border-b border-border/40 last:border-0">
                          <span className="font-medium truncate">{l.title || '(untitled)'}</span>
                          <span className="text-muted-foreground whitespace-nowrap">{l.missing.length} missing</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {migrationResult.listings_with_missing_files?.length === 0 && migrationResult.urls_rewritten > 0 && (
                  <p className="text-green-700 font-body text-sm pt-2 border-t border-border">
                    ✅ All listing images recovered successfully.
                  </p>
                )}

                {migrationResult.sample_urls?.length > 0 && (
                  <details className="pt-3 border-t border-border">
                    <summary className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground cursor-pointer">
                      Debug: sample URLs ({migrationResult.sample_urls.length})
                    </summary>
                    <ul className="mt-2 space-y-2 text-xs">
                      {migrationResult.sample_urls.map((s, i) => (
                        <li key={i} className="bg-secondary/40 p-2 break-all">
                          <div><span className="text-muted-foreground">URL:</span> {s.original}</div>
                          <div><span className="text-muted-foreground">Filename:</span> {s.extracted_filename}</div>
                          <div>
                            <span className="text-muted-foreground">Found in R2:</span>{' '}
                            <span className={s.found_in_r2 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
                              {s.found_in_r2 ? 'YES' : 'NO'}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Custom Fields Section */}
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-heading text-xl font-bold tracking-tight text-foreground">
              Custom Fields
            </h2>
            <p className="font-body text-muted-foreground mt-1">
              Configure custom fields for property listings
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="rounded-none font-heading font-bold uppercase tracking-wide bg-brand-gold text-brand-brown hover:bg-primary/90" data-testid="add-field-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Custom Field
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold">
                {editingField ? 'Edit Custom Field' : 'Add Custom Field'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="space-y-2">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Field Name *
                </Label>
                <Input
                  value={formData.field_name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData({ 
                      ...formData, 
                      field_name: name,
                      field_key: editingField ? formData.field_key : generateFieldKey(name)
                    });
                  }}
                  required
                  placeholder="e.g., Bedrooms, Square Feet"
                  className="rounded-none border-2"
                  data-testid="field-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Field Key *
                </Label>
                <Input
                  value={formData.field_key}
                  onChange={(e) => setFormData({ ...formData, field_key: e.target.value })}
                  required
                  disabled={!!editingField}
                  placeholder="e.g., bedrooms, square_feet"
                  className="rounded-none border-2 font-mono text-sm"
                  data-testid="field-key-input"
                />
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Used internally to store data. Cannot be changed after creation.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Field Type *
                </Label>
                <Select value={formData.field_type} onValueChange={(value) => setFormData({ ...formData, field_type: value })}>
                  <SelectTrigger className="rounded-none border-2" data-testid="field-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="select">Dropdown Select</SelectItem>
                    <SelectItem value="checkbox">Checkbox (Yes/No)</SelectItem>
                    <SelectItem value="date">Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.field_type === 'select' && (
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Options
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      placeholder="Add option..."
                      className="rounded-none border-2"
                      data-testid="option-input"
                    />
                    <Button type="button" onClick={addOption} variant="outline" className="rounded-none border-2" data-testid="add-option-btn">
                      Add
                    </Button>
                  </div>
                  {formData.options.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.options.map((option) => (
                        <Badge key={option} variant="secondary" className="rounded-none flex items-center gap-1">
                          {option}
                          <button type="button" onClick={() => removeOption(option)} className="hover:text-destructive">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between">
                <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Required Field
                </Label>
                <Switch
                  checked={formData.required}
                  onCheckedChange={(checked) => setFormData({ ...formData, required: checked })}
                  data-testid="field-required-switch"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button type="submit" className="rounded-none font-heading font-bold uppercase tracking-wide bg-brand-gold text-brand-brown hover:bg-primary/90" data-testid="save-field-btn">
                  {editingField ? 'Update Field' : 'Create Field'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="rounded-none border-2">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>

      {/* Info Card */}
      <div className="bg-secondary/50 p-6 border border-border">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-black text-white flex items-center justify-center shrink-0">
            <Code className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-heading font-semibold mb-1">Custom Fields</h3>
            <p className="font-body text-sm text-muted-foreground">
              Create custom fields to add additional information to your property listings. 
              These fields will appear in the listing form and on property details pages.
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50">
              <TableHead className="font-heading text-xs uppercase tracking-widest">Field Name</TableHead>
              <TableHead className="font-heading text-xs uppercase tracking-widest font-mono">Key</TableHead>
              <TableHead className="font-heading text-xs uppercase tracking-widest">Type</TableHead>
              <TableHead className="font-heading text-xs uppercase tracking-widest">Required</TableHead>
              <TableHead className="font-heading text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                </TableCell>
              </TableRow>
            ) : customFields.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12">
                  <Settings className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                  <p className="font-body text-muted-foreground">No custom fields created yet</p>
                  <p className="font-body text-sm text-muted-foreground mt-1">
                    Click "Add Custom Field" to get started
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              customFields.map((field) => (
                <TableRow key={field.id} className="hover:bg-secondary/30" data-testid={`field-row-${field.id}`}>
                  <TableCell className="font-heading font-semibold text-sm">
                    {field.field_name}
                  </TableCell>
                  <TableCell className="font-mono text-sm text-muted-foreground">
                    {field.field_key}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="rounded-none font-heading text-[10px] uppercase tracking-widest">
                      {field.field_type}
                    </Badge>
                    {field.options && field.options.length > 0 && (
                      <span className="text-xs text-muted-foreground ml-2">
                        ({field.options.length} options)
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {field.required ? (
                      <Badge className="rounded-none bg-brand-gold text-brand-brown font-heading text-[10px] uppercase tracking-widest">
                        Required
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">Optional</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(field)} className="p-2" data-testid={`edit-field-${field.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(field.id)} className="p-2 text-destructive hover:text-destructive" data-testid={`delete-field-${field.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      </div>
    </div>
  );
};

export default AdminSettings;
